<!-- eslint-disable -->
<template>
  <div class="card border rounded-4 shadow-sm h-100 w-100">
    <div class="card-body p-3 p-lg-4 d-flex flex-column gap-4 h-100">
      <div class="d-flex flex-column gap-1">
        <h2 class="fw-bold text-x mb-0">Reviewers</h2>
        <p class="text-secondary text-xs mb-0">Who reviews this form at this status?</p>
      </div>

      <div v-if="contextLocked" class="alert alert-info border-0 rounded-4 p-3 mb-0 reviewer-context-banner shadow-sm">
        <p class="fw-bold text-xs mb-2">Current Transition Context</p>
        <div class="row g-3">
          <div class="col-12 col-md-3">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Workflow</p>
            <p class="fw-bold text-xs mb-0">{{ workflowName }}</p>
          </div>
          <div class="col-12 col-md-3">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Form</p>
            <p class="fw-bold text-xs mb-0">{{ currentFormLabel }}</p>
          </div>
          <div class="col-12 col-md-4">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Transition</p>
            <p class="fw-bold text-xs mb-0">{{ transitionLabel }}</p>
          </div>
          <div class="col-12 col-md-2">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Trigger</p>
            <p class="fw-bold text-xs mb-0">{{ transitionTrigger || 'Manual' }}</p>
          </div>
        </div>
      </div>

      <ReviewerStatusSelector
        v-if="!contextLocked"
        :form-options="formOptions"
        :status-options="statusOptions"
        :selected-form-id="selectedFormId"
        :selected-status-id="selectedStatusId"
        :current-form-label="currentFormLabel"
        :current-status-label="currentStatusLabel"
        :scope-text="reviewerScopeText"
        @update:selectedFormId="$emit('update:selectedFormId', $event)"
        @update:selectedStatusId="$emit('update:selectedStatusId', $event)"
      />

      <div v-if="reviewersLoading" class="text-secondary text-xs px-2">
        Loading reviewers for the selected form status...
      </div>

      <div v-else-if="assignmentDisabled" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <p class="fw-bold text-xs mb-1">Select a form status to assign reviewers</p>
          <p class="text-secondary text-xxs mb-0">
            Reviewers are assigned per form and status, not globally for the workflow.
          </p>
        </div>
      </div>

      <div v-else-if="!reviewersRequiredForAnyStatus" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <p class="fw-bold text-xs mb-1">No reviewer setup required</p>
          <p class="text-secondary text-xxs mb-0">
            This workflow currently only contains automatic transitions.
          </p>
        </div>
      </div>

      <div v-else-if="automaticOnlyStatus" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <p class="fw-bold text-xs mb-1">No reviewer setup required</p>
          <p class="text-secondary text-xxs mb-0">
            This status currently only contains automatic transitions.
          </p>
        </div>
      </div>

      <template v-else>
        <ReviewerAssignmentForm
          :disabled="assignmentDisabled"
          :reviewer-roles-loading="reviewerRolesLoading"
          :role-users-loading="roleUsersLoading"
          :reviewers-saving="reviewersSaving"
          :assign-mode="reviewerAssignMode"
          :reviewer-role-options="reviewerRoleOptions"
          :reviewer-user-options="reviewerUserOptions"
          :selected-role-ids="selectedReviewerRoleIds"
          :selected-user-ids="selectedReviewerUserIds"
          :selected-role-option-id="selectedReviewerRoleOptionId"
          :selected-user-role-filter-id="selectedReviewerUserRoleFilterId"
          :selected-user-option-id="selectedReviewerUserOptionId"
          :required-reviewer-count="requiredReviewerCount"
          :assigned-reviewers="assignedReviewers"
          :warning-text="reviewerCountWarning"
          :reviewer-required="reviewerRequired"
          @update:requiredReviewerCount="$emit('update:requiredReviewerCount', $event)"
          @update:assignMode="$emit('update:reviewerAssignMode', $event)"
          @update:selectedRoleOptionId="$emit('update:selectedReviewerRoleOptionId', $event)"
          @update:selectedUserRoleFilterId="$emit('update:selectedReviewerUserRoleFilterId', $event)"
          @update:selectedUserOptionId="$emit('update:selectedReviewerUserOptionId', $event)"
          @load-role-users="$emit('load-role-users')"
          @toggle-reviewer-role="$emit('toggle-reviewer-role', $event)"
          @toggle-all-reviewer-roles="$emit('toggle-all-reviewer-roles')"
          @toggle-reviewer-user="$emit('toggle-reviewer-user', $event)"
          @toggle-all-reviewer-users="$emit('toggle-all-reviewer-users')"
          @add-reviewer="$emit('add-reviewer')"
          @save-reviewers="$emit('save-reviewers')"
        />

        <AssignedReviewersList
          :reviewers="assignedReviewers"
          :current-form-label="currentFormLabel"
          :current-status-label="currentStatusLabel"
          :reviewer-required="reviewerRequired"
          :empty-state-title="emptyStateTitle"
          :empty-state-message="emptyStateMessage"
          :removing-reviewer-key="removingReviewerKey"
          @remove-reviewer="$emit('remove-reviewer', $event)"
          @edit-reviewer="$emit('edit-reviewer', $event)"
          @configure-section-access="$emit('configure-section-access', $event)"
        />

        <div v-if="showApprovalOrderingSection" class="card border rounded-4 shadow-sm bg-light bg-opacity-25">
          <div class="card-body p-3 p-lg-4 d-flex flex-column gap-4">
            <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
              <div>
                <p class="fw-bold text-xs mb-1">Reviewer Approval Mode</p>
                <p class="text-secondary text-xxs mb-0">
                  Choose how reviewers should process approvals for this status.
                </p>
              </div>

              <div class="d-flex gap-2 p-1 bg-white border rounded-pill">
                <button
                  type="button"
                  class="btn btn-xs rounded-pill px-3 py-1 transition-all"
                  :class="approvalMode === 'parallel' ? 'btn-primary shadow-sm' : 'btn-link text-secondary text-decoration-none'"
                  @click="$emit('update:approvalMode', 'parallel')"
                >
                  Parallel
                </button>
                <button
                  type="button"
                  class="btn btn-xs rounded-pill px-3 py-1 transition-all"
                  :class="approvalMode === 'sequential' ? 'btn-primary shadow-sm' : 'btn-link text-secondary text-decoration-none'"
                  @click="$emit('update:approvalMode', 'sequential')"
                >
                  Sequential
                </button>
              </div>
            </div>

            <div v-if="approvalMode === 'sequential'" class="border-top pt-4">
              <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <div>
                  <p class="fw-bold text-xs mb-1">Set Approval Sequence</p>
                  <p class="text-secondary text-xxs mb-0">
                    Define the order in which reviewers must approve. Leave unordered for parallel processing.
                  </p>
                </div>

                <button
                  type="button"
                  class="btn btn-primary-dark text-xs rounded-3"
                  :disabled="approvalRulesSaving || !reviewerUserAssignments.length"
                  @click="$emit('save-approval-rules')"
                >
                  <i class="bi bi-sort-numeric-down me-2"></i>{{ approvalRulesSaving ? 'Saving Sequence...' : 'Save Approval Order' }}
                </button>
              </div>

              <div v-if="reviewerUserAssignments.length" class="d-flex flex-column gap-2">
                <div
                  v-for="reviewer in reviewerUserAssignments"
                  :key="reviewer.workflowReviewerId"
                  class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border rounded-4 px-3 py-3 bg-white shadow-sm"
                >
                  <div class="d-flex align-items-center gap-3">
                    <div class="bg-light rounded-circle d-flex align-items-center justify-content-center reviewer-order-badge">
                      <span class="fw-bold text-primary reviewer-order-value">{{ reviewer.order || '?' }}</span>
                    </div>
                    <div>
                      <p class="fw-semibold text-xs mb-0">{{ reviewer.label }}</p>
                      <p class="text-secondary text-xxs mb-0">{{ reviewer.description }}</p>
                    </div>
                  </div>

                  <div class="d-flex align-items-center gap-3">
                    <label class="text-secondary text-xxs fw-bold text-uppercase mb-0">Sequence Pos.</label>
                    <input
                      :value="reviewer.order"
                      type="number"
                      min="1"
                      class="form-control text-xs bg-light border-0 fw-bold text-center workflow-approval-order-input workflow-approval-order-input-compact"
                      @input="$emit('update-approval-order', { workflowReviewerId: reviewer.workflowReviewerId, order: Number($event.target.value || 1) })"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4 bg-white rounded-4 border border-dashed">
                <p class="text-secondary text-xxs mb-0">
                  Assign user reviewers to configure a sequential approval order.
                </p>
              </div>
            </div>

            <div v-else class="text-center py-4 border border-dashed rounded-4">
              <p class="text-secondary text-xxs mb-0">
                <i class="bi bi-info-circle me-1"></i> Parallel approvals allow all reviewers to approve at any time. No ordering required.
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, defineAsyncComponent } from 'vue';

const REVIEWERS_TAB_PROP_KEYS = new Set(['assignedReviewers', 'reviewerRequired', 'hasApprovalTransitions']);

function is_plain_record(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function has_reviewers_tab_prop(props, key) {
  const safe_key = String(key);
  const has_prop = REVIEWERS_TAB_PROP_KEYS.has(safe_key)
    && is_plain_record(props)
    && Boolean(Reflect.getOwnPropertyDescriptor(props, safe_key));
  return has_prop;
}

function reviewers_count(props) {
  const reviewers = has_reviewers_tab_prop(props, 'assignedReviewers')
    ? Reflect.get(props, 'assignedReviewers')
    : [];
  const count = Array.isArray(reviewers) ? reviewers.length : 0;
  return count;
}

function reviewers_tab_boolean(props, key) {
  const raw_value = has_reviewers_tab_prop(props, key)
    ? Reflect.get(props, String(key))
    : false;
  const boolean_value = Boolean(raw_value);
  return boolean_value;
}

const reviewers_tab_components = {
  AssignedReviewersList: defineAsyncComponent(() => import('./AssignedReviewersList.vue')),
  ReviewerAssignmentForm: defineAsyncComponent(() => import('./ReviewerAssignmentForm.vue')),
  ReviewerStatusSelector: defineAsyncComponent(() => import('./ReviewerStatusSelector.vue')),
};

export default {
  name: 'ReviewersTab',
  components: reviewers_tab_components,
  props: {
    formOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    selectedFormId: {
      type: String,
      default: '',
    },
    selectedStatusId: {
      type: String,
      default: '',
    },
    currentFormLabel: {
      type: String,
      default: 'No form selected',
    },
    currentStatusLabel: {
      type: String,
      default: 'No status selected',
    },
    reviewerScopeText: {
      type: String,
      default: '',
    },
    reviewersLoading: {
      type: Boolean,
      default: false,
    },
    reviewersSaving: {
      type: Boolean,
      default: false,
    },
    reviewerRolesLoading: {
      type: Boolean,
      default: false,
    },
    roleUsersLoading: {
      type: Boolean,
      default: false,
    },
    reviewerAssignMode: {
      type: String,
      default: 'role',
    },
    reviewerRoleOptions: {
      type: Array,
      default: () => [],
    },
    reviewerUserOptions: {
      type: Array,
      default: () => [],
    },
    selectedReviewerRoleOptionId: {
      type: String,
      default: '',
    },
    selectedReviewerRoleIds: {
      type: Array,
      default: () => [],
    },
    selectedReviewerUserIds: {
      type: Array,
      default: () => [],
    },
    selectedReviewerUserRoleFilterId: {
      type: String,
      default: '',
    },
    selectedReviewerUserOptionId: {
      type: String,
      default: '',
    },
    requiredReviewerCount: {
      type: Number,
      default: 1,
    },
    assignedReviewers: {
      type: Array,
      default: () => [],
    },
    reviewerCountWarning: {
      type: String,
      default: '',
    },
    assignmentDisabled: {
      type: Boolean,
      default: true,
    },
    reviewerUserAssignments: {
      type: Array,
      default: () => [],
    },
    approvalRulesSaving: {
      type: Boolean,
      default: false,
    },
    approvalMode: {
      type: String,
      default: 'parallel',
    },
    hasApprovalTransitions: {
      type: Boolean,
      default: false,
    },
    reviewerRequired: {
      type: Boolean,
      default: false,
    },
    automaticOnlyStatus: {
      type: Boolean,
      default: false,
    },
    reviewersRequiredForAnyStatus: {
      type: Boolean,
      default: false,
    },
    emptyStateTitle: {
      type: String,
      default: '',
    },
    emptyStateMessage: {
      type: String,
      default: '',
    },
    removingReviewerKey: {
      type: String,
      default: '',
    },
    workflowName: {
      type: String,
      default: 'Workflow',
    },
    transitionLabel: {
      type: String,
      default: 'No transition selected',
    },
    transitionTrigger: {
      type: String,
      default: '',
    },
    contextLocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:selectedFormId',
    'update:selectedStatusId',
    'update:requiredReviewerCount',
    'update:reviewerAssignMode',
    'update:selectedReviewerRoleOptionId',
    'update:selectedReviewerUserRoleFilterId',
    'update:selectedReviewerUserOptionId',
    'update:approvalMode',
    'load-role-users',
    'toggle-reviewer-role',
    'toggle-all-reviewer-roles',
    'toggle-reviewer-user',
    'toggle-all-reviewer-users',
    'add-reviewer',
    'edit-reviewer',
    'remove-reviewer',
    'configure-section-access',
    'save-reviewers',
    'update-approval-order',
    'save-approval-rules',
  ],
  setup(props) {
    const showApprovalOrderingSection = computed(() => {
      const reviewer_count = reviewers_count(props);
      const show_section = reviewers_tab_boolean(props, 'reviewerRequired')
        && reviewer_count > 1
        && reviewers_tab_boolean(props, 'hasApprovalTransitions');
      return show_section;
    });

    return { showApprovalOrderingSection };
  },
};
</script>

<style scoped>
.workflow-approval-order-input {
  width: 96px;
}

.workflow-approval-order-input-compact {
  width: 60px;
}

.reviewer-order-badge {
  width: 32px;
  height: 32px;
}

.reviewer-order-value {
  font-size: 0.75rem;
}

.reviewer-context-banner {
  background-color: rgb(32 16 63 / 5%);
  color: #20103f;
}
</style>
